import { IHttpService } from "../HttpService";
import ReportingPeriodItem, {
  IReportingPeriodItem
} from "../../model/report/ReportingPeriodItem";
import ApplicantReport, {
  IApplicantReport
} from "../../model/report/ApplicantReport";
import { DateService } from "../DateService";
import BusinessRule, {
  IBusinessRule
} from "../../model/evaluation/BusinessRule";
import Report6Mapper from "./mappers/Report6Mapper";
import Report6Applicant, {
  IReport6Applicant
} from "../../model/report/Report6Applicant";
import Report8Applicant, {
  IReport8Applicant
} from "../../model/report/Report8Applicant";
import Report8DataMapper from "./mappers/Report8DataMapper";
import FormDetailsMapper from "../form/mappers/FormDetailsMapper";
import { IFormSection } from "../../model/form/FormSection";
import FormTypeItem, { IFormTypeItem } from "../../model/report/FormTypeItem";
import { parseBooleanValue, formatBooleanValue } from "../..";
import Report1Applicant, {
  IReport1Applicant
} from "../../model/report/Report1Applicant";
import FormVersionItem, {
  IFormVersionItem
} from "../../model/report/FormVersionItem";
import Report3Applicant, {
  IReport3Applicant
} from "../../model/report/Report3Applicant";
import PublicationsReportRecord, {
  IPublicationsReportRecord
} from "../../model/report/PublicationsReportRecord";
import { IFormStructureSection } from "../../model/report/applicantdata/FormStructureSection";
import Report7Applicant, {
  IReport7Applicant
} from "../../model/report/report7/Report7Applicant";
import Report7NumericalValue, {
  IReport7NumericalValue
} from "../../model/report/report7/Report7NumericalValues";

export interface IReportService {
  getReportingPeriodList(): Promise<IReportingPeriodItem[]>;
  getReport3Data(
    reportingPeriodId: number,
    periodFrom: Date,
    periodTo: Date,
    min: number,
    max: number,
    submitReason?: string
  ): Promise<IReport3Applicant[]>;
  getDataReport5(rpeortingPeriodId: number): Promise<IApplicantReport[]>;
  getRiskCriteria(reportingPeriodId: number): Promise<IBusinessRule[]>;
  getDataReport4(
    rpeortingPeriodId: number,
    riskCriteriaList: number[]
  ): Promise<IApplicantReport[]>;
  getReport2Data(
    periodFrom: Date,
    periodTo: Date,
    min: number,
    max: number,
    evaluationStatus?: string
  ): Promise<IApplicantReport[]>;
  getDataReport8(
    reportingPeriodId: number,
    applicantIdsList: number[]
  ): Promise<any>;
  getReport8ApplicantList(fullName: string): Promise<IReport8Applicant[]>;
  getReport6Data(
    applicantIds: number[],
    reportingPeriodId?: number
  ): Promise<IReport6Applicant[]>;
  getApplicantList(fullName: string): Promise<IReport6Applicant[]>;
  getReport1ApplicantList(fullName: string): Promise<IReport1Applicant[]>;
  getReport1Data(
    formVersionId: number,
    includePublicAttribute: boolean
  ): Promise<IFormSection[]>;
  getFormTypeList(reportingPeriodId: number): Promise<IFormTypeItem[]>;
  getFormVersionsList(
    reportingPeriodId: number,
    applicantId: number,
    formTypeId: number
  ): Promise<IFormVersionItem[]>;
  getPublicationsReportData(): Promise<IPublicationsReportRecord[]>;
  getFormStructure(formTypeCode: string): Promise<IFormStructureSection[]>;
  getReport7Data(
    reportingPeriodId: number,
    applicantIds: number[],
    formTypeCode: string
  ): Promise<{
    applicants: IReport7Applicant[];
    numericalValues: IReport7NumericalValue[];
  }>;
}

const ReportService = (httpService): IReportService => {
  const _http: IHttpService = httpService;
  const _basePath: string = "/api/reports";
  const _reportingPeriodsPath: string = "/reporting-periods";
  const _report5Path: string = "/report5";
  const _report3Path: string = "/report3";
  const _riskCriteriaPath: string = "/risk-criteria";
  const _report4Path: string = "/report4";
  const _report1Path: string = "/report1";
  const _report2Path: string = "/report2";
  const _report8Path: string = "/report8";
  const _report6Path: string = "/report6";
  const _report7Path: string = "/report7";
  const _applicantsPath: string = "/applicants";
  const _formTypesPath: string = "/form-types";
  const _formVersionsPath: string = "/form-versions";
  const _formVersionPath: string = "/form-version";
  const _publicationsPath: string = "/publications";
  const _formStructuresPath: string = "/form-structures";

  const ReportingPeriodListMapper = (json = []) => {
    return json.map((item: any) => ReportingPeriodItem(item));
  };
  const RiskCriteraListMapper = (json = []) => {
    return json.map((item: any) => BusinessRule(item));
  };
  const FormTypeListMapper = (json = []) => {
    return json.map((item: any) => FormTypeItem(item));
  };
  const ApplicantReportListMapper = (json = []) => {
    return json.map((item: any) => ApplicantReport(item));
  };

  const parseDateToTimestamp = (date: Date) => {
    return DateService().parseDateToTimestamp(date);
  };

  const Report8ApplicantsMapper = (json = []) => {
    return json.map((item: any) => Report8Applicant(item));
  };

  const ApplicantsMapper = (json = []) => {
    return json.map((item: any) => Report6Applicant(item));
  };

  const Report1ApplicantsMapper = (json = []) => {
    return json.map((item: any) => Report1Applicant(item));
  };
  const FormVersionListMapper = (json = []) => {
    return json.map((item: any) => FormVersionItem(item));
  };

  const Report3ApplicantListMapper = (json = []) => {
    return json.map((item: any) => Report3Applicant(item));
  };

  function Report7ApplicantsListMapper(json: any[] = []) {
    return json.map((item: any) => {
      return Report7Applicant(item);
    });
  }

  function Report7NumericalValuesListMapper(json: any[] = []) {
    return json.map((item: any) => {
      return Report7NumericalValue(item);
    });
  }

  const PublicationsReportRecordMapper = (json = []) => {
    return json.map((item: any) => {
      let publicationReportRecord = PublicationsReportRecord(item);
      publicationReportRecord.additionalEvaluationIndicator = formatBooleanValue(
        item.additionalEvaluationIndicator
      );
      return publicationReportRecord;
    });
  };

  return {
    async getReportingPeriodList() {
      const path = _http.buildPath(_basePath, _reportingPeriodsPath);
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return ReportingPeriodListMapper(responseJSON);
    },

    async getReport3Data(
      reportingPeriodId: number,
      periodFrom: Date,
      periodTo: Date,
      min: number,
      max: number,
      submitReason?: string
    ) {
      const path = _http.buildPath(
        _basePath,
        _report3Path,
        _reportingPeriodsPath,
        reportingPeriodId.toString()
      );
      const response = await _http.get(path, {
        form: true,
        params: {
          periodFrom: parseDateToTimestamp(periodFrom),
          periodTo: parseDateToTimestamp(periodTo),
          min,
          max,
          submitReason
        }
      });
      const responseJSON = await _http.toJSON(response);
      return Report3ApplicantListMapper(responseJSON);
    },
    async getReport1Data(
      formVersionId: number,
      includePublicAttribute: boolean
    ) {
      const path = _http.buildPath(
        _basePath,
        _report1Path,
        _formVersionPath,
        formVersionId.toString()
      );
      const response = await _http.get(path, {
        form: true,
        params: {
          formVersionId: formVersionId,
          includePublicAttribute: parseBooleanValue(includePublicAttribute)
        }
      });
      const responseJSON = await _http.toJSON(response);
      const {
        formSections: sections,
        formSectionQualifiers: qualifiers,
        qualifierAttributes: attributes,
        formInputValues: values
      } = await responseJSON;

      return FormDetailsMapper(sections, qualifiers, attributes, values);
    },

    async getDataReport5(reportingPeriodId: number) {
      const path = _http.buildPath(
        _basePath,
        _report5Path,
        _reportingPeriodsPath,
        reportingPeriodId.toString()
      );
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return ApplicantReportListMapper(responseJSON);
    },

    async getRiskCriteria(reportingPeriodId: number) {
      const path = _http.buildPath(
        _basePath,
        _riskCriteriaPath,
        _reportingPeriodsPath,
        reportingPeriodId.toString()
      );
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return RiskCriteraListMapper(responseJSON);
    },

    async getDataReport4(
      reportingPeriodId: number,
      riskCriterionList: number[]
    ) {
      const path = _http.buildPath(
        _basePath,
        _report4Path,
        _reportingPeriodsPath,
        reportingPeriodId.toString()
      );
      const response = await _http.get(path, {
        form: true,
        params: {
          riskCriterionList: riskCriterionList
        }
      });
      const responseJSON = await _http.toJSON(response);
      return ApplicantReportListMapper(responseJSON);
    },

    async getReport2Data(
      periodFrom: Date,
      periodTo: Date,
      min: number,
      max: number,
      evaluationStatus?: string
    ) {
      const path = _http.buildPath(_basePath, _report2Path);
      const response = await _http.get(path, {
        form: true,
        params: {
          periodFrom: parseDateToTimestamp(periodFrom),
          periodTo: parseDateToTimestamp(periodTo),
          min,
          max,
          evaluationStatus
        }
      });
      const responseJSON = await _http.toJSON(response);
      return ApplicantReportListMapper(responseJSON);
    },

    async getReport6Data(applicantIds: number[], reportingPeriodId?: number) {
      const path = _http.buildPath(_basePath, _report6Path);
      const response = await _http.get(path, {
        form: true,
        params: {
          applicantIds,
          reportingPeriodId
        }
      });
      const responseJSON = await _http.toJSON(response);
      const {
        applicants,
        reportingPeriods,
        reports,
        riskCriterias
      } = await responseJSON;
      return Report6Mapper(
        applicants,
        reportingPeriods,
        reports,
        riskCriterias
      );
    },
    async getDataReport8(
      reportingPeriodId: number,
      applicantIdsList: number[]
    ) {
      const path = _http.buildPath(
        _basePath,
        _report8Path,
        _reportingPeriodsPath,
        reportingPeriodId.toString()
      );
      const response = await _http.get(path, {
        form: true,
        params: {
          applicantIdsList: applicantIdsList
        }
      });
      const responseJSON = await _http.toJSON(response);
      const {
        applicantRecord: applicants,
        relativesWorkingInCourt: relatives,
        additionalIncome,
        nonPaidActivities,
        giftsAndDonations: gifts,
        debtObligations: debt
      } = await responseJSON;

      return Report8DataMapper(
        applicants,
        relatives,
        additionalIncome,
        nonPaidActivities,
        gifts,
        debt
      );
    },

    async getApplicantList(fullName: string) {
      const path = _http.buildPath(_basePath, _applicantsPath);
      const response = await _http.get(path, {
        params: {
          fullName
        },
        form: true
      });
      const responseJSON = await _http.toJSON(response);
      return ApplicantsMapper(responseJSON);
    },

    async getFormTypeList(reportingPeriodId: number) {
      const path = _http.buildPath(_basePath, _formTypesPath);
      const response = await _http.get(path, {
        form: true,
        params: {
          reportingPeriodId: reportingPeriodId
        }
      });
      const responseJSON = await _http.toJSON(response);
      return FormTypeListMapper(responseJSON);
    },

    async getReport8ApplicantList(fullName: string) {
      const path = _http.buildPath(_basePath, _applicantsPath);
      const response = await _http.get(path, {
        params: {
          fullName
        },
        form: true
      });
      const responseJSON = await _http.toJSON(response);
      return Report8ApplicantsMapper(responseJSON);
    },
    async getReport1ApplicantList(fullName: string) {
      const path = _http.buildPath(_basePath, _applicantsPath);
      const response = await _http.get(path, {
        params: {
          fullName
        },
        form: true
      });
      const responseJSON = await _http.toJSON(response);
      return Report1ApplicantsMapper(responseJSON);
    },
    async getFormVersionsList(
      reportingPeriodId: number,
      applicantId: number,
      formTypeId: number
    ) {
      const path = _http.buildPath(_basePath, _formVersionsPath);
      const response = await _http.get(path, {
        form: true,
        params: {
          reportingPeriodId: reportingPeriodId,
          applicantId: applicantId,
          reportingPeriodFormTypeId: formTypeId
        }
      });
      const responseJSON = await _http.toJSON(response);
      return FormVersionListMapper(responseJSON);
    },
    async getPublicationsReportData() {
      const path = _http.buildPath(_basePath, _publicationsPath);
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return PublicationsReportRecordMapper(responseJSON);
    },

    async getFormStructure(formTypeCode: string) {
      const path = _http.buildPath(
        _basePath,
        _report7Path,
        _formStructuresPath,
        formTypeCode
      );
      const response = await _http.get(path);
      return _http.toJSON(response);
    },

    async getReport7Data(
      reportingPeriodId: number,
      applicantIds: number[],
      formTypeCode: string
    ) {
      const path = _http.buildPath(_basePath, _report7Path);
      const response = await _http.get(path, {
        params: {
          reportingPeriodId,
          applicantIds,
          formTypeCode
        },
        form: true
      });
      const responseJSON = await _http.toJSON(response);
      const { applicants, numericalValues } = await responseJSON;
      return {
        applicants: Report7ApplicantsListMapper(applicants),
        numericalValues: Report7NumericalValuesListMapper(numericalValues)
      };
    }
  };
};

export default ReportService;
